import { initRuntime, SeedTokenProviderWrapperStrategy, DafInMemoryCacheStrategy, getShellActions, loadApp } from '@nab/x-runtime';
import {
    DafAnonymousAuthStrategy,
    DafExchangeStrategy,
    DafGrantType,
} from '@nab/x-auth-core';
import config from './config.json';

const shellClientId = config.LoginAppConfig.clientId;
const dafEndpoint = config.LoginAppConfig.dafEndpoint;

const logoutConfig = config.LogoutConfig || {};

const loginRedirectPath = '/Login.aspx';

const logoutCallbacks = new Set();

let resolveShellActionsReady;
let rejectShellActionsReady;
const shellActionsReady = new Promise((resolve, reject) => {
    resolveShellActionsReady = resolve;
    rejectShellActionsReady = reject; 
});

let sessionTimeoutTimerId;

function clearSessionTimeoutTimer() {
    if (sessionTimeoutTimerId) {
        window.clearTimeout(sessionTimeoutTimerId);
        sessionTimeoutTimerId = undefined;
    }
}
const sessionCookieNames = ['Bearer', 'RTNClientBearer'];

function getCookieDomains() {
    // A cookie carrying a Domain attribute is a distinct cookie from the
    // host-only one of the same name, so each scope needs its own expiry.
    // Missing one leaves a duplicate that the browser keeps sending and the
    // server reads in preference to the freshly issued token.
    const domains = [undefined];
    const host = window.location.hostname || '';

    if (!host || host === 'localhost' || /^\d+(\.\d+){3}$/.test(host)) {
        return domains;
    }

    const labels = host.split('.');
    for (let i = 0; i < labels.length - 1; i += 1) {
        domains.push(labels.slice(i).join('.'));
    }
    return domains;
}

function getCookiePaths() {
    // '/' is how the shell and RealTimeNostro write these cookies; the
    // directory chain covers anything written from a sub-path such as /_ASP.
    const paths = ['/'];
    let current = '';
    (window.location.pathname || '')
        .split('/')
        .filter(Boolean)
        .forEach((segment) => {
            current += `/${segment}`;
            paths.push(current);
        });
    return paths;
}

function expireCookie(name) {
    const expires = new Date(0).toUTCString();

    getCookiePaths().forEach((path) => {
        getCookieDomains().forEach((domain) => {
            const attributes = [`${name}=`, `expires=${expires}`, `path=${path}`];
            if (domain) {
                attributes.push(`domain=${domain}`);
            }
            document.cookie = attributes.join(';');
        });
    });
}

function clearBearerToken() {
    sessionStorage.removeItem('bearerToken');
    sessionCookieNames.forEach(expireCookie);
}


function isSameOriginPath(value) {
    if (typeof value !== 'string') {
        return false;
    }
    const path = value.trim();
    // Only a path on this host. An absolute URL, a protocol-relative URL, or a
    // backslash (the URL parser treats it as a slash) is not a local path.
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
        return false;
    }
    try {
        return new URL(path, window.location.origin).origin === window.location.origin;
    } catch (error) {
        return false;
    }
}

function getLogoutRedirectUrl() {
    // Only the host app can destroy its server session and expire cookies the
    // browser will not let script touch, so prefer its logout endpoint when it
    // advertises one (RealTimeNostro sets window.DAF_LOGOUT_URL in its master
    // page). Accept only a same-origin path. Anything else, including an
    // absolute URL, falls back to the login page.
    const fallback = new URL(loginRedirectPath, window.location.origin).toString();
    const hostLogoutUrl =
        typeof window.DAF_LOGOUT_URL === 'string' ? window.DAF_LOGOUT_URL.trim() : '';
    const configuredPath =
        typeof logoutConfig.logoutPath === 'string' ? logoutConfig.logoutPath.trim() : '';
    const target = hostLogoutUrl || configuredPath || loginRedirectPath;
    if (!isSameOriginPath(target)) {
        return fallback;
    }
    return new URL(target, window.location.origin).toString();
}



function redirectAfterLogout() {
    const redirectUrl = getLogoutRedirectUrl();
    if (window.location.href === redirectUrl) {
        window.location.reload();
        return;
    }
    window.location.assign(redirectUrl);
}


function readCookie(name) {
    const prefix = `${name}=`;
    const match = (document.cookie || '')
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
}

// Authenticated host pages start a fresh shell. The login page already put
// the token in sessionStorage, but hydrate from the visible Bearer cookie
// so Change Password still has a seed token if sessionStorage was empty.
function hydrateBearerTokenFromCookie() {
    if (sessionStorage.getItem('bearerToken')) {
        return;
    }
    const bearer = readCookie('Bearer');
    if (bearer) {
        sessionStorage.setItem('bearerToken', bearer);
    }
}

function persistBearerToken(data) {
    if (!data?.bearerToken) {
        return;
    }

    sessionStorage.setItem('bearerToken', data.bearerToken);

    // Expire every scope first. Writing only path=/ leaves a domain-scoped
    // duplicate in place, and the server reads that stale Bearer instead of
    // the new host-only value — both cookies then appear unchanged.
    expireCookie('Bearer');
    // Secure and SameSite keep this token off plain HTTP and off cross-site
    // requests. HttpOnly is omitted because Change Password reads the cookie
    // when sessionStorage is empty.
    document.cookie = `Bearer=${data.bearerToken};path=/;Secure;SameSite=Lax`;
}

function getDafApiUrl() {
    if (logoutConfig.dafApiUrl) {
        return logoutConfig.dafApiUrl;
    }
    try {
        return new URL(dafEndpoint).origin;
    } catch (error) {
        console.warn('Unable to derive DAF API URL for logout.', error);
        return '';
    }
}

function notifyLogoutConsumers() {
    logoutCallbacks.forEach((callback) => {
        try {
            callback();
        } catch (error) {
            console.error('Logout callback failed.', error);
        }
    });
}

// Revocation has to finish before we navigate. Fired as keepalive and forgotten,
// it can reach the IdP *after* the user has signed back in, and revoking an access
// token takes the underlying grant with it - so the brand new token gets killed and
// the next page fails its introspect check. Bounded so a slow IdP cannot trap the
// user in a session they asked to leave.
const REVOKE_TIMEOUT_MS = 3000;

async function revokeBearerToken(bearerToken, dafApiUrl, clientId) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REVOKE_TIMEOUT_MS);

    try {
        await fetch(`${dafApiUrl}/v1/idp/oauth/revoke`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: bearerToken, client_id: clientId }),
            keepalive: true,
            signal: controller.signal,
        });
    } catch (error) {
        console.warn('Token revocation did not complete before logout.', error);
    } finally {
        clearTimeout(timeout);
    }
}

export async function performLogout() {
    const bearerToken = sessionStorage.getItem('bearerToken');
    const dafApiUrl = getDafApiUrl();
    const clientId = logoutConfig.clientId || shellClientId;

    clearSessionTimeoutTimer();
    clearBearerToken();
    sessionStorage.clear();

    notifyLogoutConsumers();
    window.dispatchEvent(new CustomEvent('signout'));

    if (bearerToken && dafApiUrl) {
        await revokeBearerToken(bearerToken, dafApiUrl, clientId);
    }

    redirectAfterLogout();
}

export function renderLogoutButton() {
    const hostElement = document.getElementById('Logout');
    if (!hostElement) {
        console.warn('Logout host element not found.');
        return;
    }

    hostElement.innerHTML = '';

    const button = document.createElement('button');
    button.id = 'logoutButton';
    button.type = 'button';
    button.className = 'logout-btn';
    button.textContent = 'Log Out';

    let loggingOut = false;
    button.addEventListener('click', () => {
        if (loggingOut) {
            return;
        }
        loggingOut = true;
        button.disabled = true;
        performLogout();
    });

    hostElement.appendChild(button);
}

async function loadOptionalApp({ appMetaData, appConfig, hostElement, authConfig }) {
    if (!hostElement) {
        console.warn(`Host element not found for app ${appMetaData.id}`);
        return;
    }

    try {
        await loadApp({
            appMetaData,
            appConfig,
            hostElement,
            authConfig,
        });
    } catch (error) {
        console.warn(`Skipping app ${appMetaData.id}: unable to read asset manifest.`, error);
    }
}

class ShellSeedTokenProviderWrapperStrategy extends SeedTokenProviderWrapperStrategy {
    async authenticate(config) {
        return {
            config: {
                ...config,
                appMetaData: {
                    additionalContexts: undefined,
                    appType: 'XV2SPA',
                    contextId: 'open-global',
                    id: 'SHELL',
                    tag: 'x-root-spa-default'
                }
            },
            tokenValue: sessionStorage.getItem('bearerToken')
        };
    }
}

export default async function startShell() {
    try {
        hydrateBearerTokenFromCookie();
        const authTokenCacheStrategy = new DafInMemoryCacheStrategy();
        await initRuntime({
            authTokenCacheStrategy,
            rootContextId: 'root',
            authConfig: {
                environment: config.LoginAppConfig.environment,
                context: 'external',
                tokenType: 'Bearer',
                tokenSource: 'DAF',
                clientId: shellClientId,
                scopes: [config.LoginAppConfig.scope],
                requestTokenOnLoad: false,
                addTelemetryHeader: true,
            },
            authStrategies: [
                new DafAnonymousAuthStrategy(dafEndpoint, shellClientId),
                new DafExchangeStrategy(dafEndpoint),
                new ShellSeedTokenProviderWrapperStrategy(() => {
                    return Promise.resolve('');
                }),
            ],
            servicesConfig: {
                bmp: {
                    endpointToProtect: 'nab.com.au'
                }
            },
            userConfig: {
                type: 'customer',
                org: 'NAB',
            },
            brand: {
                id: 'wholesale',
                themeName: 'nab',
            },
        });

    

        const shellActions = getShellActions();
        resolveShellActionsReady(shellActions);
        
        shellActions.addEventListener('user_authenticated', async data => {
            persistBearerToken(data);
        },
    );

        shellActions.addEventListener('sign_out', () => {
            clearSessionTimeoutTimer();
            clearBearerToken();
            console.log('Logged out');
        });


        shellActions.addEventListener('user_logout_request', () => {
             clearSessionTimeoutTimer();
             clearBearerToken();
             console.log('Logged out');
        });


        const loginHost = document.getElementById('Login');
        // MasterPage reuses id="Login" on the hidden disclaimer button. Only
        // mount the login miniapp into a real container (Login.aspx).
        if (loginHost && loginHost.tagName !== 'BUTTON') {
            loadApp({
                appMetaData: config.LoginAppMetaData,
                appConfig: config.LoginAppConfig,
                hostElement: loginHost,
                authConfig: {
                    grantType: DafGrantType.nabAnonymous,
                },
            });
        }

        renderLogoutButton();

        await loadOptionalApp({
            appMetaData: config.ChangePwdAppMetaData,
            appConfig: config.ChangePwdAppConfig,
            hostElement: document.getElementById('ChangePassword'),
            authConfig: {
                requestTokenOnLoad: true,
            },
        });

    } catch (error) {
        rejectShellActionsReady(error);
        console.error('Failed to start shell', error);
        throw error;
    }
}

export function LoginSubscribe(callback) {
    shellActionsReady
        .then((shellActions) => {
            shellActions.addEventListener('user_authenticated', (data) => {
                persistBearerToken(data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            });
        })
        .catch((error) => {
            console.error('Unable to subscribe to user_authenticated.', error);
        });
}

export function LogoutSubscribe(callback) {
    if (typeof callback === 'function') {
        // Register for shell-owned logout (logout button -> performLogout -> notifyLogoutConsumers)
        logoutCallbacks.add(callback);
    }

    // Preserve the existing runtime-driven logout path (shellActions sign_out)
    shellActionsReady
        .then((shellActions) => {
            shellActions.addEventListener('sign_out', () => {
                console.log('Logged out - callback');
                if (typeof callback === 'function') {
                    callback();
                }
            });
        })
        .catch((error) => {
            console.error('Unable to subscribe to sign_out.', error);
        });
}

LoginSubscribe.bind(document);
document.userAuthenticationCallback = LoginSubscribe;

LogoutSubscribe.bind(document);
document.userLogoutCallback = LogoutSubscribe;
